/**
 * Timeline synthesis — turns a run of completed tool parts into ONE
 * human-readable rollup line for the build-timeline conversation view.
 *
 * "Tool sandbox.exec Completed" ×6 becomes:
 *   "Project scaffolded · Dependencies installed · 4 files written · Dev server running"
 *
 * Pure module: no React, no DOM. Everything here is derived from tool
 * names + inputs/outputs that already live in the message parts — this is
 * a RENDERING synthesis, not a data change.
 *
 * Two levels of description:
 *   • `describeToolPart(part)`      — one phrase for one tool call (used for
 *                                     the per-step rows inside the expanded
 *                                     timeline entry).
 *   • `synthesizeToolSummary(parts)` — the aggregated rollup for the
 *                                     collapsed entry (merges counts:
 *                                     "4 files written", "3 commands run").
 *
 * Rule of the house: NEVER surface a raw camelCase/dotted tool id. Unknown
 * tools fall back to the tool-display registry title, then to a humanized
 * form of the name.
 */

import { resolveDelegatedToolDisplay } from '@/lib/capabilities/display';
import { getToolDisplay, getToolStateTitle } from '@/lib/tool-display';

export interface TimelineToolPart {
  toolName: string;
  input?: unknown;
  output?: unknown;
  /** true when the part's state was 'output-error' */
  errored?: boolean;
}

/**
 * Tools that currently render standalone cards in the chat but are really
 * agent WORK, not user-facing artifacts — terminal runs, file reads, source
 * greps. These fold into the aggregated timeline entry once completed.
 *
 * Deliberately NOT here: live surfaces the user interacts with directly
 * (browser live view, sandbox preview, dashboards, askUserQuestion, media
 * players, rendered visualizations) — those stay visible as cards.
 */
export const COLLAPSIBLE_WORK_TOOLS = new Set<string>([
  'sandboxExec',
  'sandboxCodeRun',
  'sandboxSessionExec',
  'sandboxSessionLogs',
  'sandboxListFiles',
  'sandboxReadFile',
  'browserListFiles',
  'browserReadFile',
  'readGalexiaFile',
  'readGalexiaFileLines',
  'listMyDocuments',
  'viewDocument',
  'getVisualizationSource',
  'readVisualizationLines',
  'grepVisualization',
]);

export function isCollapsibleWorkTool(toolName: string, input?: unknown, output?: unknown): boolean {
  const delegated = resolveDelegatedToolDisplay(toolName, input, output);
  return COLLAPSIBLE_WORK_TOOLS.has(delegated.toolName);
}

// =============================================================================
// Small helpers
// =============================================================================

function truncate(s: string, max: number): string {
  const clean = s.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function basename(path: string): string {
  const parts = path.replace(/\/+$/, '').split('/');
  return parts[parts.length - 1] || path;
}

function domainOf(url: string): string | undefined {
  try {
    const u = new URL(url.includes('://') ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

/** "fooBarBaz" / "foo_bar" / "foo.bar-baz" → "Foo bar baz" */
export function humanizeToolName(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._\-]+/g, ' ')
    .toLowerCase()
    .trim();
  if (!words) return 'Tool';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Did this call fail? Covers output-error state, success:false, *_failed codes. */
function didToolFail(part: TimelineToolPart, output: unknown): boolean {
  if (part.errored) return true;
  const o = asRecord(output);
  if (o.success === false) return true;
  const code = str(o.code);
  if (code && /(_failed|_error)$/i.test(code)) return true;
  return false;
}

// =============================================================================
// Command purpose inference (sandbox exec family)
// =============================================================================

/**
 * Infer a human purpose from a shell command. Returns undefined when the
 * command doesn't clearly match a known purpose (caller falls back to gist).
 * Order matters: scaffold beats install beats dev-server, so
 * "npm init && npm install vite" reads as scaffolding, and "npm install vite"
 * doesn't trip the vite-dev-server check.
 */
export function inferCommandPurpose(command: string): string | undefined {
  const c = command.toLowerCase();
  if (/\b(?:npm|pnpm|yarn|bun)\s+init\b|npx\s+create[-\s]|npm\s+create\b|pnpm\s+create\b|yarn\s+create\b|create-(?:vite|next|react)-?/.test(c)) {
    return 'Project scaffolded';
  }
  if (/tailwind/.test(c) && /\b(install|init|add)\b/.test(c)) {
    return 'Tailwind configured';
  }
  if (/\bnpm\s+i(?:nstall)?\b|\bpnpm\s+(?:add|install)\b|\byarn\s+add\b|\bbun\s+(?:add|install)\b|\bpip3?\s+install\b|\bapt(?:-get)?\s+install\b/.test(c)) {
    return 'Dependencies installed';
  }
  if (/\bvitest\b|\bjest\b|\bpytest\b|\b(?:npm|pnpm|yarn|bun)\s+(?:run\s+)?test\b|\bgo\s+test\b/.test(c)) {
    return 'Tests run';
  }
  if (/\b(?:npm|pnpm|yarn|bun)\s+run\s+build\b|\bvite\s+build\b|\bnext\s+build\b|\btsc\b|\bwebpack\b/.test(c)) {
    return 'Build completed';
  }
  if (/\b(?:npm|pnpm|yarn|bun)\s+(?:run\s+)?(?:dev|start)\b|\bnext\s+dev\b|\bvite\b(?!\s*build)|\bnodemon\b|\buvicorn\b/.test(c)) {
    return 'Dev server running';
  }
  if (/\bgit\s+clone\b/.test(c)) return 'Repo cloned';
  if (/\bgit\s+commit\b/.test(c)) return 'Changes committed';
  if (/\bgit\s+push\b/.test(c)) return 'Changes pushed';
  return undefined;
}

// =============================================================================
// Per-part description
// =============================================================================

type MergeKind =
  | 'command-generic'
  | 'file-write'
  | 'file-read'
  | 'file-list'
  | 'screenshot'
  | 'search'
  | 'image-search'
  | 'image-gen'
  | 'browser-visit'
  | 'skill-read';

interface Unit {
  phrase: string;
  /** Units with the same merge kind collapse into a counted phrase. */
  merge?: MergeKind;
  /** For image batches etc. — how many artifacts this one call produced. */
  weight?: number;
}

const MERGED_PHRASES: Record<MergeKind, (n: number, first: string, distinct: string[]) => string> = {
  'command-generic': (n, first) => (n === 1 ? first : `${n} commands run`),
  'file-write': (n, first) => (n === 1 ? first : `${n} files written`),
  'file-read': (n, first) => (n === 1 ? first : `Read ${n} files`),
  'file-list': (_n, first) => first, // "Listed files" doesn't scale with count
  screenshot: (n, first) => (n === 1 ? first : `Captured ${n} screenshots`),
  search: (n, first) => (n === 1 ? first : `${n} web searches`),
  'image-search': (n, first) => (n === 1 ? first : `${n} image searches`),
  'image-gen': (n) => (n === 1 ? 'Image generated' : `${n} images generated`),
  // Distinct pages, not counts: "Visited a.com, b.com" (repeat visits dedupe).
  'browser-visit': (_n, _first, distinct) => {
    const domains = distinct.map((p) => p.replace(/^Visited /, ''));
    const shown = domains.slice(0, 3);
    const extra = domains.length - shown.length;
    return `Visited ${shown.join(', ')}${extra > 0 ? ` +${extra} more` : ''}`;
  },
  'skill-read': (n, first) => (n === 1 ? first : `Read ${n} skills`),
};

function classify(toolName: string, input: unknown, output: unknown): Unit {
  const i = asRecord(input);
  const o = asRecord(output);

  switch (toolName) {
    // ---- sandbox commands -------------------------------------------------
    case 'sandboxExec':
    case 'sandboxSessionExec': {
      const command = str(i.command);
      const purpose = command ? inferCommandPurpose(command) : undefined;
      if (purpose) return { phrase: purpose };
      return {
        phrase: command ? `Ran \`${truncate(command, 44)}\`` : 'Command run',
        merge: 'command-generic',
      };
    }
    case 'sandboxCodeRun': {
      const lang = str(i.language);
      return { phrase: lang ? `Ran ${lang} code` : 'Ran code', merge: 'command-generic' };
    }
    case 'sandboxSessionLogs':
      return { phrase: 'Checked logs' };

    // ---- file writes / edits ---------------------------------------------
    case 'sandboxWriteFile':
    case 'browserWriteFile':
    case 'writeGalexiaTextFile': {
      const path = str(i.path) ?? str(i.filePath);
      return { phrase: path ? `Wrote ${basename(path)}` : 'File written', merge: 'file-write' };
    }
    case 'replaceGalexiaFileText': {
      const path = str(i.path) ?? str(i.filePath);
      return { phrase: path ? `Edited ${basename(path)}` : 'File edited', merge: 'file-write' };
    }
    case 'sandboxCreateFolder': {
      const path = str(i.path);
      return { phrase: path ? `Created ${basename(path)}/` : 'Folder created' };
    }

    // ---- file reads ---------------------------------------------------------
    case 'sandboxReadFile':
    case 'browserReadFile':
    case 'readGalexiaFile':
    case 'readGalexiaFileLines': {
      const path = str(i.path) ?? str(i.filePath);
      return { phrase: path ? `Read ${basename(path)}` : 'Read file', merge: 'file-read' };
    }
    case 'viewDocument':
      return { phrase: str(i.title) ? `Read “${truncate(i.title as string, 32)}”` : 'Read document', merge: 'file-read' };

    // ---- file listing / search ---------------------------------------------
    case 'sandboxListFiles':
    case 'browserListFiles':
    case 'listGalexiaFiles':
      return { phrase: 'Listed files', merge: 'file-list' };
    case 'listMyDocuments':
      return { phrase: 'Listed documents', merge: 'file-list' };
    case 'sandboxSearchFiles':
    case 'searchGalexiaFiles':
    case 'grepVisualization': {
      const q = str(i.query) ?? str(i.pattern);
      return { phrase: q ? `Searched files: “${truncate(q, 28)}”` : 'Searched files' };
    }
    case 'getVisualizationSource':
    case 'readVisualizationLines':
      return { phrase: 'Read source', merge: 'file-read' };

    // ---- web / image search -------------------------------------------------
    case 'webSearch': {
      const q = str(i.query);
      return { phrase: q ? `Searched: “${truncate(q, 36)}”` : 'Searched the web', merge: 'search' };
    }
    case 'imageSearch': {
      const q = str(i.query);
      return { phrase: q ? `Image search: “${truncate(q, 32)}”` : 'Searched images', merge: 'image-search' };
    }
    case 'imageSearchBatch': {
      const n = Array.isArray(i.queries) ? i.queries.length : undefined;
      return { phrase: `Image search · ${n ?? 'multiple'} queries`, merge: 'image-search', weight: n ?? 1 };
    }

    // ---- browser -------------------------------------------------------------
    case 'createBrowser':
    case 'browserNavigate': {
      const url = str(i.url) ?? str(i.startUrl) ?? str(asRecord(o.data).currentUrl) ?? str(o.currentUrl);
      const domain = url ? domainOf(url) : undefined;
      if (domain) return { phrase: `Visited ${domain}`, merge: 'browser-visit' };
      return { phrase: 'Opened browser' };
    }
    case 'browserScroll':
      return { phrase: 'Scrolled page' };
    case 'browserScreenshot':
      return { phrase: 'Captured screenshot', merge: 'screenshot' };
    case 'browserPlaywright': {
      const code = str(i.code) ?? '';
      const gotoMatch = code.match(/goto\(\s*['"`]([^'"`]+)['"`]/);
      const domain = gotoMatch ? domainOf(gotoMatch[1]) : undefined;
      if (domain) return { phrase: `Visited ${domain}`, merge: 'browser-visit' };
      return { phrase: 'Ran browser script', merge: 'command-generic' };
    }

    // ---- media generation ------------------------------------------------------
    case 'generateImage':
      return { phrase: 'Image generated', merge: 'image-gen' };
    case 'generateImageBatch': {
      const n = Array.isArray(i.items) ? i.items.length : 1;
      return { phrase: `${n} images generated`, merge: 'image-gen', weight: n };
    }
    case 'generateSpeech':
      return { phrase: 'Voiceover generated' };
    case 'generateNarrationPlan':
      return { phrase: 'Narration planned' };
    case 'generateMusic':
      return { phrase: 'Music generated' };
    case 'generateSoundEffect':
    case 'generateSoundEffectBatch':
      return { phrase: 'Sound effects generated' };
    case 'generateVideoClip':
    case 'createSceneVideo':
      return { phrase: 'Video clip generated' };
    case 'generateAvatarClip':
      return { phrase: 'Avatar clip generated' };

    // ---- deploy -------------------------------------------------------------
    case 'deployApp': {
      const version = typeof o.version === 'number' ? o.version : undefined;
      return { phrase: version !== undefined ? `Deployed v${version}` : 'Deployed' };
    }
    case 'rollbackApp': {
      const version = typeof o.version === 'number' ? o.version : undefined;
      return { phrase: version !== undefined ? `Rolled back to v${version}` : 'Rolled back' };
    }
    case 'registerBuiltApp':
      return { phrase: 'App saved' };

    // ---- skills / memory / reference -----------------------------------------
    case 'readSkill': {
      const slug = str(i.slug);
      return { phrase: slug ? `Read skill: ${slug}` : 'Read skill', merge: 'skill-read' };
    }
    case 'listSkills':
      return { phrase: 'Listed skills' };
    case 'listCapabilities':
      return { phrase: 'Checked capabilities' };
    case 'listUserMemories':
      return { phrase: 'Listed memories' };
    case 'viewFullMemory':
      return { phrase: 'Read memory' };
    case 'createUserMemory':
      return { phrase: 'Saved memory' };
    case 'updateUserMemory':
      return { phrase: 'Updated memory' };
    case 'deleteUserMemory':
      return { phrase: 'Removed memory' };
    case 'viewHistory':
      return { phrase: 'Read chat history' };
    case 'readTurn':
      return { phrase: typeof i.turnIndex === 'number' ? `Read turn ${i.turnIndex}` : 'Read chat turn' };
    case 'readToolResult':
      return { phrase: 'Read tool result' };
    case 'listVisualizationVersions':
      return { phrase: 'Listed versions' };
    case 'viewVisualization':
      return { phrase: str(o.title) ? `Viewed “${truncate(o.title as string, 32)}”` : 'Viewed visualization' };
    case 'viewImages':
      return { phrase: 'Inspected images' };
    case 'transcribeVideo':
    case 'getVideoTranscript':
      return { phrase: 'Video transcribed' };
    case 'suggestInteraction':
      return { phrase: 'Internal feedback' };

    default: {
      // Registry fallback: the completed-state title reads well ("Command
      // finished", "Read file"). If the registry itself falls back to the
      // raw tool name, humanize it — never leak camelCase ids.
      const display = getToolDisplay(toolName);
      const title = getToolStateTitle(toolName, 'completed');
      if (display.label !== toolName && title !== toolName) return { phrase: title };
      return { phrase: humanizeToolName(toolName) };
    }
  }
}

/**
 * One-phrase description of a single completed tool call. Resolves delegated
 * callTool/callCapability wrappers first.
 */
export function describeToolPart(part: TimelineToolPart): string {
  const delegated = resolveDelegatedToolDisplay(part.toolName, part.input, part.output);
  return classify(delegated.toolName, delegated.input ?? part.input, delegated.output ?? part.output).phrase;
}

// =============================================================================
// The rollup
// =============================================================================

const MAX_PHRASES = 4;
const MAX_SUMMARY_CHARS = 160;

/**
 * Synthesize a human-readable one-line rollup from a run of completed tool
 * parts. Merges per-family counts, dedupes repeats, keeps first-occurrence
 * order, appends failure count, and caps length.
 *
 *   [exec init, exec install, write ×4, exec dev] →
 *   "Project scaffolded · Dependencies installed · 4 files written · Dev server running"
 */
export function synthesizeToolSummary(parts: TimelineToolPart[]): string {
  if (!Array.isArray(parts) || parts.length === 0) return '';

  interface Bucket {
    order: number;
    merge?: MergeKind;
    firstPhrase: string;
    count: number;
    weight: number;
    /** Distinct phrases seen for this merge family, in order. */
    distinct: string[];
  }
  const buckets: Bucket[] = [];
  const byMerge = new Map<MergeKind, Bucket>();
  const byPhrase = new Map<string, Bucket>();
  let failed = 0;

  for (const part of parts) {
    if (!part || typeof part.toolName !== 'string' || !part.toolName) continue;
    const delegated = resolveDelegatedToolDisplay(part.toolName, part.input, part.output);
    const input = delegated.input ?? part.input;
    const output = delegated.output ?? part.output;

    // Check both the raw and the delegated output: callTool wrappers report
    // failure codes on the OUTER envelope ({ code: 'sandbox_command_failed',
    // data: {...} }) while the delegated output is just the inner data.
    if (didToolFail(part, part.output) || didToolFail(part, output)) {
      failed += 1;
      continue;
    }

    const unit = classify(delegated.toolName, input, output);
    const weight = unit.weight ?? 1;

    // Merge by family (counted phrases), else dedupe identical phrases.
    const existing = unit.merge ? byMerge.get(unit.merge) : byPhrase.get(unit.phrase);
    if (existing) {
      existing.count += 1;
      existing.weight += weight;
      if (!existing.distinct.includes(unit.phrase)) existing.distinct.push(unit.phrase);
      continue;
    }
    const bucket: Bucket = {
      order: buckets.length,
      merge: unit.merge,
      firstPhrase: unit.phrase,
      count: 1,
      weight,
      distinct: [unit.phrase],
    };
    buckets.push(bucket);
    if (unit.merge) byMerge.set(unit.merge, bucket);
    else byPhrase.set(unit.phrase, bucket);
  }

  const phrases = buckets.map((b) => {
    if (b.merge) return MERGED_PHRASES[b.merge](b.weight, b.firstPhrase, b.distinct);
    return b.count > 1 ? `${b.firstPhrase} ×${b.count}` : b.firstPhrase;
  });

  const shown = phrases.slice(0, MAX_PHRASES);
  const hidden = phrases.length - shown.length;
  if (hidden > 0) shown.push(`+${hidden} more`);
  if (failed > 0) shown.push(failed === 1 ? '1 step failed' : `${failed} steps failed`);

  if (shown.length === 0) return '';
  return truncate(shown.join(' · '), MAX_SUMMARY_CHARS);
}

/**
 * Total weighted work-unit count across a run of completed tool parts — the
 * SAME units synthesizeToolSummary's phrases are built from (an
 * imageSearchBatch call with 3 queries counts as 3, not 1). Used for the
 * timeline's step-count badge so it never disagrees with the summary line —
 * "3 image searches" next to "2 STEPS" was the bug (CHAT_UI_DESIGN_REVIEW.md
 * §4 item 2: "make the meta count the same unit as the summary"). A failed
 * call still counts as one unit, matching synthesizeToolSummary's failure
 * tally.
 */
export function countToolWorkUnits(parts: TimelineToolPart[]): number {
  if (!Array.isArray(parts) || parts.length === 0) return 0;
  let total = 0;
  for (const part of parts) {
    if (!part || typeof part.toolName !== 'string' || !part.toolName) continue;
    const delegated = resolveDelegatedToolDisplay(part.toolName, part.input, part.output);
    const input = delegated.input ?? part.input;
    const output = delegated.output ?? part.output;
    if (didToolFail(part, part.output) || didToolFail(part, output)) {
      total += 1;
      continue;
    }
    total += classify(delegated.toolName, input, output).weight ?? 1;
  }
  return total;
}
